import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { AuthService } from '@services/auth/auth.service';
import { SessionStorageService } from '@services/session-storage/session-storage.service';

const SEVEN_DAYS_IN_SECONDS = 7 * 86400;
const SESSION_KEY_PREFIX = 'feature_access_banner_dismissed_';

/**
 * Topbar banner warning the dashboard owner that a support-applied feature grant
 * (ClickUp 86c6zuk48) is about to expire and revert to their plan default. Reads
 * the already-streamed customer document via AuthService — no extra request — and
 * keys dismissal on the expiry timestamp so a renewed grant surfaces again.
 */
@Component({
    selector: 'app-feature-access-banner',
    imports: [RouterLink, TranslateModule, DatePipe],
    templateUrl: './feature-access-banner.component.html',
    styleUrl: './feature-access-banner.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeatureAccessBannerComponent {
    private dismissedExpiries = signal<Set<number>>(new Set());

    readonly expiresAt = computed(() => this.authService.customerFirestore()?.additional_access_expires_at ?? null);

    readonly showBanner = computed(() => {
        const expiresAt = this.expiresAt();
        if (expiresAt === null) return false;

        const now = dayjs().unix();
        const isExpiringSoon = expiresAt - now <= SEVEN_DAYS_IN_SECONDS && expiresAt > now;
        const isDismissed = this.dismissedExpiries().has(expiresAt) || !!this.sessionStorageService.getItem(`${SESSION_KEY_PREFIX}${expiresAt}`);
        return isExpiringSoon && !isDismissed;
    });

    constructor(
        private authService: AuthService,
        private sessionStorageService: SessionStorageService
    ) {}

    dismiss(): void {
        const expiresAt = this.expiresAt();
        if (expiresAt === null) return;
        this.sessionStorageService.setItem(`${SESSION_KEY_PREFIX}${expiresAt}`, 'true');
        this.dismissedExpiries.update(set => new Set(set).add(expiresAt));
    }
}
